<template>
  <div class="uk-container test-results">
    <UserHeader />
    <h2>
      <span v-if="this.typeDescription === 'Pets'">Pet </span>Intolerance Kit - {{ this.typeName }}
    </h2>
    <h1>Results for
      <span v-if="petName">{{ this.petName }}</span>
      <span v-else>{{ this.firstName }} {{ this.lastName }}</span>
    </h1>
    <div class="uk-margin-bottom report-date">Tested on {{ convertUtcToLocalDateOnly(this.resultDateUtc) }}</div>

    <div v-if="ingredientCheckerHasFreeEntitlement" class="uk-alert-success uk-padding-small uk-margin-bottom no-print">
      <strong>{{ ingredientCheckerEntitlementLabel }}</strong>
      <span v-if="ingredientCheckerEntitlementEndsUtc">
        · Available through {{ formatEntitlementDate(ingredientCheckerEntitlementEndsUtc) }}
      </span>
      <router-link
        :to="{ path: '/ingredient-checker', query: { id: kitId } }"
        class="uk-button uk-button-secondary uk-button-small uk-margin-small-left"
      >
        Open Ingredient Checker
      </router-link>
    </div>

    <div class="how-card uk-margin-bottom no-print">
          <div class="how-step">
            <div class="how-step-num">01</div>
            <div class="how-step-title">Understanding your results</div>
            <div v-if="isPetResult" class="how-step-desc">
              First, it's important to know this is a list of intolerances—not allergies. According to the American Kennel Club, many pet parents assume their pet has an allergy when it's really an intolerance. Intolerances are harder to spot but can still cause issues like itchiness, a dull coat, digestive problems, or recurring paw and ear trouble.
            </div>
            <div v-else class="how-step-desc">
              This test identifies <strong>intolerances</strong>, not allergies. Items that appear here may be quietly contributing to bloating, fatigue, or skin issues — not immediate reactions.
            </div>
            <div style="display:flex; gap:6px; flex-wrap:wrap; margin-top:6px;">
              <span class="sev-pill red"><span class="dot"></span> Avoid for now</span>
              <span class="sev-pill amber"><span class="dot"></span> Reduce</span>
              <span class="sev-pill green"><span class="dot"></span> You're clear</span>
            </div>
          </div>
          <div class="how-step">
            <div class="how-step-num">02</div>
            <div class="how-step-title">What to do with it</div>
            <div v-if="isPetResult" class="how-step-desc">
              Helping your pet feel better may involve adjusting food, treats, shampoos, or supplements. Take it slow. A week-long transition helps avoid stomach upset.
            </div>
            <div v-else class="how-step-desc">
              Start by removing <strong>Severe</strong> items for 2–3 weeks. You don't need to overhaul everything at once. Track how you feel — many people notice changes within days.
            </div>
            <ul class="how-step-list">
              <li>Filter to <strong>Severe only</strong></li>
              <li>Choose a category — <strong>Food</strong> is a great start</li>
              <li><strong>Save or print</strong> your list to use while shopping</li>
            </ul>
          </div>
          <div class="how-step">
            <div class="how-step-num">03</div>
            <div class="how-step-title">A note on context</div>
            <div v-if="isPetResult" class="how-step-desc">
              If your pet is reacting strongly, it's worth a vet visit to rule out other issues.
            </div>
            <div v-else class="how-step-desc">
              You may see items you eat daily with no obvious issues — overexposure can build up quietly. If something you know causes problems isn't flagged, it may be a true allergy, not an intolerance. Always consult your healthcare provider for major changes.
            </div>
          </div>
        </div>

    <div class="uk-flex uk-flex-middle uk-flex-between no-print test-results-nav uk-margin-top">
      <div class="filter-box uk-flex-1 uk-flex uk-flex-left">
        <ul uk-tab>
          <li :class="{ 'uk-active': level === 'all-sensitivities' }" @click="level = 'all-sensitivities'" uk-tooltip="All Sensitivities" class="all"><a href="#">All</a></li>
          <li :class="{ 'uk-active': level === 'severe-sensitivity' }" @click="level = 'severe-sensitivity'" uk-tooltip="Severe" class="severe-only"><a href="#">Severe</a></li>
          <li :class="{ 'uk-active': level === 'moderate-sensitivity' }" @click="level = 'moderate-sensitivity'" uk-tooltip="Moderate Sensitivity" class="moderate-only"><a href="#">Moderate</a></li>
          <li :class="{ 'uk-active': level === 'normal-sensitivity' }" @click="level = 'normal-sensitivity'" uk-tooltip="Normal Sensitivity" class="normal-only"><a href="#">Normal</a></li>
        </ul>

      </div>

      <div class="uk-flex uk-flex-middle">
        <div class="search-box uk-flex uk-flex-middle uk-margin-right">
          <input v-model="search" @keyup.enter="fetchItems" placeholder="Search item" class="uk-input" />
          <button v-if="search" @click="clearSearch" class="clear-button">×</button>
        </div>
        <div class="uk-flex uk-flex-middle uk-margin-right test-result-button" @click="view = 'summary'">
          <img
            :src="view === 'summary' ? '/images/summary-view.svg' : '/images/summary-view-inactive.svg'"
            alt="Summary View"
            uk-tooltip="Summary View"
            width="20"
            height="20"
            class="uk-margin-small-right"
          />
          Summary
        </div>
        <div class="uk-flex uk-flex-middle test-result-button" @click="view = 'list'">
          <img
            :src="view === 'list' ? '/images/list-view.svg' : '/images/list-view-inactive.svg'"
            alt="List View"
            uk-tooltip="List View"
            width="20"
            height="20"
            class="uk-margin-small-right"
          />
          List
        </div>
      </div>

      <div class="uk-margin-left uk-flex uk-flex-middle print-option">
        <div class="uk-flex uk-flex-middle uk-margin-small-right">
          <a href="#filter-offcanvas" uk-toggle uk-tooltip="Filter">
            <img
              src="/images/filter.svg"
              alt="Filter"
              width="20"
              height="20"
            />
          </a>
        </div>

        <div class="uk-flex uk-flex-middle uk-margin-small-right" @click="printPage()">
          <img
            src="/images/print.svg"
            alt="Print"
            width="20"
            height="20"
            uk-tooltip="Print"
          />
        </div>

        <div
          v-if="resultDateUtc && kitId"
          class="uk-flex uk-flex-middle uk-margin-small-right"
        >
          <a v-if="!loadingPDF[kitId]" href="#" @click.prevent="openPdfOptionsModal()">
            <img
              src="/images/pdf.svg"
              alt="Download PDF"
              width="20"
              height="20"
              uk-tooltip="Download PDF"
              style="cursor: pointer;"
            />
          </a>
          <span v-else>
            <span uk-spinner="ratio: 0.6"></span>
          </span>
        </div>
      </div>
    </div>

    <div class="uk-flex uk-flex-middle uk-flex-between no-print test-results-nav uk-margin-small-top">
      <div class="uk-margin">
        <ul class="uk-tab uk-margin-small-top" uk-tab>
          <li :class="{ 'uk-active': category === 'All' }" @click="category = 'All'"><a href="#">All</a></li>
          <li v-for="categoryName in visibleCategoryNames" :key="categoryName" :class="{ 'uk-active': category === categoryName }" @click="category = categoryName">
            <a href="#">{{ displayCategoryName(categoryName) }}</a>
          </li>
        </ul>
      </div>
    </div>

    <div v-if="isLoading" class="uk-flex uk-flex-center uk-margin-top">
      <div uk-spinner="ratio: 1"></div>
    </div>

    <div v-if="renderReady" class="test-results-content-layout" uk-grid>
      <div class="uk-width-expand@l">
        <div class="uk-flex uk-flex-wrap uk-flex-middle uk-flex-between uk-grid-small" uk-grid style="display: none;">

        <!-- Sensitivity Tabs -->
        <div class="uk-flex-auto uk-overflow-auto no-print test-results-nav">
          <ul class="uk-tab" uk-tab>
            <li :class="{ 'uk-active': level === 'all-sensitivities' }" @click="level = 'all-sensitivities'" uk-tooltip="All Sensitivities"><a href="#">All</a></li>
            <li :class="{ 'uk-active': level === 'severe-moderate-sensitivities' }" @click="level = 'severe-moderate-sensitivities'" uk-tooltip="Severe & Moderate Sensitivities"><a href="#">Severe & Moderate</a></li>
            <li :class="{ 'uk-active': level === 'severe-sensitivity' }" @click="level = 'severe-sensitivity'" uk-tooltip="Severe"><a href="#">Severe</a></li>
            <li :class="{ 'uk-active': level === 'moderate-sensitivity' }" @click="level = 'moderate-sensitivity'" uk-tooltip="Moderate Sensitivity"><a href="#">Moderate</a></li>
            <li :class="{ 'uk-active': level === 'normal-sensitivity' }" @click="level = 'normal-sensitivity'" uk-tooltip="Normal Sensitivity"><a href="#">Normal</a></li>
          </ul>
        </div>

        <!-- Category Tabs -->
        <div class="uk-flex-auto uk-overflow-auto no-print test-results-nav uk-margin-left">
          <ul class="uk-tab" uk-tab>
            <li :class="{ 'uk-active': category === 'All' }" @click="category = 'All'"><a href="#">All</a></li>
            <li v-for="categoryName in visibleCategoryNames" :key="categoryName" :class="{ 'uk-active': category === categoryName }" @click="category = categoryName">
              <a href="#">{{ displayCategoryName(categoryName) }}</a>
            </li>
          </ul>
        </div>

        <!-- Group Select -->
        <div class="uk-flex-none uk-margin-left">
          <select class="uk-select uk-form-small" v-model="group">
            <option value="All">All Groups</option>
            <option v-for="g in uniqueGroupsList" :key="g" :value="g">{{ g }}</option>
          </select>
        </div>
      </div>

        <div v-if="view === 'summary'">
          <div
            v-for="section in visibleSeverityData"
            :key="section.key"
            class="test-result-section"
          >      
          <div class="test-label-container2">
            <!-- Left: icon + label -->
            <div class="uk-flex uk-flex-middle">
              <!--
              <div class="circle uk-margin-small-right">
                <img :src="section.icon" />
              </div>
              -->
              <h3 :class="['uk-margin-remove', section.key]">
                {{ section.label }}
              </h3>
              <span :class="['section-badge', section.key]">{{section.badge}}</span>
            </div>

            <!-- Right: right-aligned instruction -->
            <div class="uk-text-right uk-text-small">
              Click an item to view the description
            </div>
          </div>

          <div class="test-result-body">
            <div class="uk-grid-match uk-child-width-expand@s" uk-grid>
              <div v-if="section.data.length">
                <div v-for="group in section.data" :key="group.CategoryName" class="uk-margin-bottom">
                  <h4>{{ group.CategoryName }}</h4>
                  <div v-if="group.items && group.items.length">
                    <!-- Group items by GroupName -->
                    <div v-for="(items, groupName) in groupItemsByGroupName(group.items)" :key="groupName">
                      <h5 v-if="groupName">{{ groupName }}</h5>

                      <div
                        class="item"
                        v-for="item in getVisibleItems(items, section.key, group.CategoryName, groupName)"
                        :key="item.ItemId || item.ItemName"
                        :uk-tooltip="getDescription(item)"
                      >
                        {{ item.ItemNameFriendly || item.ItemName }}
                      </div>

                      <div
                        v-if="getHiddenCount(items) > 0 && !isGroupExpanded(section.key, group.CategoryName, groupName)"
                        class="item item-more"
                        @click="toggleGroup(section.key, group.CategoryName, groupName)"
                      >
                        +{{ getHiddenCount(items) }} more
                      </div>

                      <div
                        v-if="getHiddenCount(items) > 0 && isGroupExpanded(section.key, group.CategoryName, groupName)"
                        class="item item-more item-less"
                        @click="toggleGroup(section.key, group.CategoryName, groupName)"
                      >
                        Show less
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else>No items found!</div>
            </div>
          </div>

          <div
            v-if="section.key === 'severe'"
            class="test-results-inline-promos"
          >
            <IngredientCheckerPromo source="test-results" />
            <NutrableProgramPromo v-if="showNutrable" source="test-results" />
            <PetVetPromo v-if="isPetResult" source="test-results" />
            <WellnessJourneyPromo />
          </div>
        </div>

        <div
          v-if="!hasVisibleSevereSection"
          class="test-results-inline-promos"
        >
          <IngredientCheckerPromo source="test-results" />
          <NutrableProgramPromo v-if="showNutrable" source="test-results" />
          <PetVetPromo v-if="isPetResult" source="test-results" />
          <WellnessJourneyPromo />
        </div>

        </div>
        <div v-else-if="view === 'list'" class="test-results-list-view">
          <table class="uk-table uk-table-divider test-results-list">
          <thead>
            <tr>
              <th class="uk-width-small">Level</th>
              <th class="uk-width-small">Category</th>
              <th class="uk-width-small">Group</th>
              <th class="uk-width-small">Item</th>
              <th v-if="showDescriptions">Description</th>
            </tr>
          </thead>
          <tbody>
            <template 
              v-if="level === 'all-sensitivities' 
                || level === 'severe-moderate-sensitivities'
                || level === 'severe-sensitivity'"
            >
              <template v-for="category in filteredDataSevere" :key="category.CategoryName">
                <tr v-for="item in category.items" :key="item.ItemName">
                  <td class="uk-width-small"><img src="/images/severe.svg" class="level" /> Severe</td>
                  <td class="uk-width-small">{{ category.CategoryName }}</td>
                  <td class="uk-width-small">{{ item.GroupName }}</td>
                  <td class="uk-width-small item-name">{{ item.ItemNameFriendly ? item.ItemNameFriendly : item.ItemName }}</td>
                  <td v-if="showDescriptions">{{ getDescription(item) }}</td>
                </tr>
              </template>
            </template>
            <template 
              v-if="level === 'all-sensitivities' 
                || level === 'severe-moderate-sensitivities'
                || level === 'moderate-sensitivity'"
            >
              <template v-for="category in filteredDataMild" :key="category.CategoryName">
                <tr v-for="item in category.items" :key="item.ItemName">
                  <td class="uk-width-small"><img src="/images/mild.svg" class="level" /> Moderate</td>
                  <td class="uk-width-small">{{ category.CategoryName }}</td>
                  <td class="uk-width-small">{{ item.GroupName }}</td>
                  <td class="uk-width-small">{{ item.ItemNameFriendly ? item.ItemNameFriendly : item.ItemName }}</td>
                  <td v-if="showDescriptions">{{ getDescription(item) }}</td>
                </tr>
              </template>
            </template>
            <template v-if="level === 'all-sensitivities' || level === 'normal-sensitivity'">
              <template v-for="category in filteredDataNormal" :key="category.CategoryName">
                <tr v-for="item in category.items" :key="item.ItemName">
                  <td class="uk-width-small"><img src="/images/normal.svg" class="level" /> Normal</td>
                  <td class="uk-width-small">{{ category.CategoryName }}</td>
                  <td class="uk-width-small">{{ item.GroupName }}</td>
                  <td class="uk-width-small">{{ item.ItemNameFriendly ? item.ItemNameFriendly : item.ItemName }}</td>
                  <td v-if="showDescriptions">{{ getDescription(item) }}</td>
                </tr>
              </template>
            </template>
          </tbody>
          </table>

          <div class="test-results-inline-promos">
            <IngredientCheckerPromo source="test-results" />
            <NutrableProgramPromo v-if="showNutrable" source="test-results" />
            <PetVetPromo v-if="isPetResult" source="test-results" />
            <WellnessJourneyPromo />
          </div>
        </div>
      </div>

      <aside class="uk-width-1-3@l uk-width-2-5@m test-results-promo-rail">
        <IngredientCheckerPromo source="test-results" />
        <NutrableProgramPromo v-if="showNutrable" source="test-results" />
        <PetVetPromo v-if="isPetResult" source="test-results" />
      </aside>
    </div>

    <div class="uk-margin-top">
      View <a :href="`/all-test-items?id=${searchTerm}`">complete list of items</a> you were tested for.
    </div>

    <div class="uk-margin-top">
      <h3>UCARI Results Disclaimer</h3>
      <p>
          We are excited to provide your personalized UCARI Intolerance Test Results. As
          you review your results, it’s important to remember that the UCARI test identifies intolerances ONLY – not allergies.
      </p>
      <p>
          The terms allergy and sensitivity/intolerance are often confused and used
          interchangeably, though incorrectly. An
          allergy is not an intolerance, and vice versa. Therefore, they must be treated differently.
      </p>
      <p>
          Having a food sensitivity/intolerance may be uncomfortable and can cause
          annoying or debilitating symptoms, but
          not life-threatening ones, like those caused by a food allergy. By taking the UCARI
          test, utilizing your results, and making the
          suggested changes to your wellness routine, your newly identified intolerances
          may be reduced or eliminated over time.
      </p>
      <p>
          A food allergy tends to be a lifelong, life threatening condition. The
          physiological process, which takes place
          in the body
          during an allergic reaction, is also entirely different than an intolerance
          reaction. An allergic reaction involves the immune
          system, whereas an intolerance affects the digestive system. The UCARI test does
          not test antibody levels, so it cannot test
          for
          allergies. For example, if you have a nut allergy, but a nut sensitivity does
          not show up in your UCARI Intolerance results,
          it
          absolutely does not mean that you can resume eating nuts.
      </p>
      <p>
          If you would like more information, please consult your doctor or email
          care@ucari.com.
      </p>
      <p>
          UCARI always recommends following the advice of your medical
          practitioners.
      </p>
    </div>
  </div>

  <div id="filter-offcanvas" uk-offcanvas="mode: slide; overlay: true; flip: true">
    <div class="uk-offcanvas-bar uk-offcanvas-bar-light">

      <button class="uk-offcanvas-close" type="button" uk-close></button>

      <h4 class="uk-margin-bottom">Filter Results</h4>

      <!-- Group Dropdown -->
      <div class="uk-margin">
        <label class="uk-form-label filter-label">Group</label>
        <select class="uk-select" v-model="group">
          <option value="All">All Groups</option>
          <option v-for="g in uniqueGroupsList" :key="g" :value="g">{{ g }}</option>
        </select>
      </div>

      <!-- Reset Button -->
      <div class="uk-margin-top">
        <button class="uk-button uk-width-1-1 reset-filter" @click="resetFilters">
          Reset Filters
        </button>
      </div>
    </div>
  </div>

  <div id="pdf-options-modal" uk-modal>
    <div class="uk-modal-dialog uk-modal-body pdf-options-modal">
      <button class="uk-modal-close-default" type="button" uk-close></button>
      <h4 class="uk-modal-title pdf-options-title">PDF Options</h4>
      <p class="pdf-options-copy">Choose what to include in the downloaded report.</p>

      <div class="pdf-preset-row">
        <button
          :class="['uk-button', 'pdf-preset-button', selectedPdfPreset === 'summary' ? 'pdf-preset-button-active' : 'uk-button-default']"
          type="button"
          :aria-pressed="selectedPdfPreset === 'summary'"
          @click="applyPdfPreset('summary')"
        >
          <span v-if="selectedPdfPreset === 'summary'" class="pdf-preset-selected" aria-hidden="true"></span>
          <span>Summary</span>
        </button>
        <button
          :class="['uk-button', 'pdf-preset-button', selectedPdfPreset === 'detailed' ? 'pdf-preset-button-active' : 'uk-button-default']"
          type="button"
          :aria-pressed="selectedPdfPreset === 'detailed'"
          @click="applyPdfPreset('detailed')"
        >
          <span v-if="selectedPdfPreset === 'detailed'" class="pdf-preset-selected" aria-hidden="true"></span>
          <span>Detailed</span>
        </button>
        <button
          :class="['uk-button', 'pdf-preset-button', selectedPdfPreset === 'full' ? 'pdf-preset-button-active' : 'uk-button-default']"
          type="button"
          :aria-pressed="selectedPdfPreset === 'full'"
          @click="applyPdfPreset('full')"
        >
          <span v-if="selectedPdfPreset === 'full'" class="pdf-preset-selected" aria-hidden="true"></span>
          <span>Full report</span>
        </button>
      </div>

      <div class="uk-margin-top">
        <div class="uk-display-block pdf-option-checkbox">
          <input
            v-model="pdfOptions.includeDescriptions"
            class="uk-checkbox uk-margin-small-right"
            type="checkbox"
            @change="persistPdfOptions()"
          />
          <span>Include item descriptions</span>
        </div>

        <div class="uk-display-block pdf-option-checkbox uk-margin-small-top">
          <input
            v-model="pdfOptions.includeNormalSensitivity"
            class="uk-checkbox uk-margin-small-right"
            type="checkbox"
            @change="persistPdfOptions()"
          />
          <span>Include normal sensitivities</span>
        </div>
      </div>

      <div class="uk-flex uk-flex-right uk-flex-middle uk-margin-top">
        <button class="uk-button pdf-cancel-button uk-margin-small-right" type="button" @click="closePdfOptionsModal()">
          Cancel
        </button>
        <button class="uk-button uk-button-primary pdf-download-button" type="button" :disabled="isCurrentPdfLoading" @click="downloadSelectedPDF()">
          <span v-if="isCurrentPdfLoading" uk-spinner="ratio: 0.55" class="uk-margin-small-right"></span>
          <span>{{ isCurrentPdfLoading ? 'Preparing PDF...' : 'Download PDF' }}</span>
        </button>
      </div>
    </div>
  </div>

  <UcariFooter />
</template>

<script>
import axios from 'axios';
import UserHeader from './UserHeader.vue'; 
import UcariFooter from './UcariFooter.vue'; 
import IngredientCheckerPromo from '@/components/IngredientCheckerPromo.vue';
import NutrableProgramPromo from '@/components/NutrableProgramPromo.vue';
import { canAccessNutrable } from '@/utils/nutrable';
import WellnessJourneyPromo from '@/components/WellnessJourneyPromo.vue';
import PetVetPromo from '@/components/PetVetPromo.vue';
import { convertUtcToLocalDateOnly, shouldHideIngredientChecker } from '@/utils/common';

const PDF_OPTIONS_STORAGE_KEY = 'ucari-test-results-pdf-options';

export default {
  name: 'TestResults',
  created() {
    document.title = 'Test Kits | UCARI';
    this.loadCredentials();
    canAccessNutrable().then(allowed => { this.showNutrable = allowed; });
    this.loadPdfOptions();
    const id = this.$route.query.id;
    if (id) {
      this.searchTerm = id;
      this.kitId = id;
      this.fetchData();
    }
  },
  components: {
    UserHeader,  
    UcariFooter,
    IngredientCheckerPromo,
    NutrableProgramPromo,
    WellnessJourneyPromo,
    PetVetPromo
  },
  data() {
    return {
      data: [],      
      dataSevere: [],
      dataMild: [],
      dataNormal: [],
      groupedSevere: [],
      groupedMild: [],
      groupedNormal: [],
      showNutrable: false,
      showFilterPanel: false,
      isLoading: false,
      error: false,
      firstName: '',
      lastName: '',
      petName: '',
      resultDateUtc: '',
      testTypeId: null,
      TypeName: '',
      TypeDescription: '',
      searchTerm: '',
      search: '',
      cid: '',
      accessToken: '',
      view: 'summary',
      level: 'all-sensitivities',
      category: 'All',
      group: 'All',
      uniqueGroupsList: [],
      isExpandedPet: false,
      isExpandedHuman: false,
      showDescriptions: true,
      renderReady: false,
      kitId: null,
      serialNumber: '',
      ingredientCheckerHasFreeEntitlement: false,
      ingredientCheckerEntitlementLabel: '',
      ingredientCheckerEntitlementEndsUtc: null,
      loadingPDF: {},
      expandedGroups: {},
      pdfOptions: {
        includeDescriptions: false,
        includeNormalSensitivity: false
      },
    };
  },
  computed: {
    visibleCategoryNames() {
      return [...new Set([...this.dataSevere, ...this.dataMild, ...this.dataNormal]
        .map(item => item.CategoryName)
        .filter(Boolean))].sort();
    },
    isPetResult() {
      return Boolean(this.petName) || String(this.typeDescription || '').toLowerCase() === 'pets';
    },
    hasVisibleSevereSection() {
      return this.visibleSeverityData.some((section) => section.key === 'severe');
    },
    isIngredientCheckerEligible() {
      if (shouldHideIngredientChecker(this.cid)) return false;
      return !!this.resultDateUtc && !!this.kitId;
    },
    ingredientCheckerTitle() {
      return this.ingredientCheckerHasFreeEntitlement
        ? this.ingredientCheckerEntitlementLabel
        : 'Ingredient Checker';
    },
    ingredientCheckerDescription() {
      return this.ingredientCheckerHasFreeEntitlement
        ? "This kit includes Free Ingredient Checker access. Snap an ingredient label and compare it to your UCARI results. We'll flag Red and Yellow matches, or let you know the product looks compatible."
        : "Snap an ingredient label and compare it to your UCARI results. We'll flag Red and Yellow matches, or let you know the product looks compatible.";
    },
    ingredientCheckerCta() {
      return this.ingredientCheckerHasFreeEntitlement ? 'Open Free Ingredient Checker' : 'Open Ingredient Checker';
    },
    visibleSeverityData() {
      return [
        {
          key: 'severe',
          data: this.filteredDataSevere,
          label: 'Severe Sensitivity',
          icon: '/images/severe.svg',
          showGroupName: true,
          badge: 'Avoid for now',
          show: ['all-sensitivities', 'severe-moderate-sensitivities', 'severe-sensitivity']
        },
        {
          key: 'moderate',
          data: this.filteredDataMild,
          label: 'Moderate Sensitivity',
          icon: '/images/mild.svg',
          showGroupName: true,
          badge: 'Reduce & monitor',
          show: ['all-sensitivities', 'severe-moderate-sensitivities', 'moderate-sensitivity']
        },
        {
          key: 'normal',
          data: this.filteredDataNormal,
          label: 'Normal Sensitivity',
          icon: '/images/normal.svg',
          showGroupName: false,
          badge: 'No action needed',
          show: ['all-sensitivities', 'normal-sensitivity']
        }
      ].filter(section => section.show.includes(this.level));
    },  
    filteredDataSevere() {
      return this.groupedSevere.map(group => ({
        CategoryName: group.CategoryName,
        items: group.items.filter(item =>
          item.ItemName.toLowerCase().includes(this.search.toLowerCase())
        )
      })).filter(group => group.items.length > 0);
    },
    filteredDataMild() {
      return this.groupedMild.map(group => ({
        CategoryName: group.CategoryName,
        items: group.items.filter(item =>
          item.ItemName.toLowerCase().includes(this.search.toLowerCase())
        )
      })).filter(group => group.items.length > 0);
    },
    filteredDataNormal() {
      return this.groupedNormal.map(group => ({
        CategoryName: group.CategoryName,
        items: group.items.filter(item =>
          item.ItemName.toLowerCase().includes(this.search.toLowerCase())
        )
      })).filter(group => group.items.length > 0);
    },
    selectedPdfPreset() {
      if (!this.pdfOptions.includeDescriptions && !this.pdfOptions.includeNormalSensitivity) {
        return 'summary';
      }

      if (this.pdfOptions.includeDescriptions && !this.pdfOptions.includeNormalSensitivity) {
        return 'detailed';
      }

      if (this.pdfOptions.includeDescriptions && this.pdfOptions.includeNormalSensitivity) {
        return 'full';
      }

      return null;
    },
    isCurrentPdfLoading() {
      return Boolean(this.kitId && this.loadingPDF[String(this.kitId)]);
    },
    isAllowedPDF() {
      const allowedCids = [
        'BA4A53C0-BEDD-4CAC-A485-A3C08497B0C0',
        'CF469D00-BEF1-4A16-81E5-40E7D1A4764D',
        'D06C1B8D-80B9-49D3-B756-EA8E68B295A1',
        'A495433D-65E0-4EE2-8506-E27A7CDEB124',
        '8C5052CD-0EDC-4914-83FB-D5D04008C62D'
      ];      
      return allowedCids.includes(this.cid);
    },    
  },
  methods: {
    displayCategoryName(categoryName) {
      if (categoryName === 'Nutritional') return 'Nutrition';
      if (categoryName === 'Plant/Environmental') return 'Environment';
      return categoryName;
    },
    loadCredentials() {
      this.accessToken = localStorage.getItem('accessToken');
      this.cid = localStorage.getItem('cid');
    },
    loadPdfOptions() {
      try {
        const savedOptions = localStorage.getItem(PDF_OPTIONS_STORAGE_KEY);
        if (!savedOptions) return;

        const parsedOptions = JSON.parse(savedOptions);
        this.pdfOptions = {
          includeDescriptions: Boolean(parsedOptions.includeDescriptions),
          includeNormalSensitivity: Boolean(parsedOptions.includeNormalSensitivity)
        };
      } catch (error) {
        console.warn('Failed to load saved PDF options:', error);
      }
    },
    persistPdfOptions() {
      try {
        localStorage.setItem(PDF_OPTIONS_STORAGE_KEY, JSON.stringify(this.pdfOptions));
      } catch (error) {
        console.warn('Failed to persist PDF options:', error);
      }
    },
    applyPdfPreset(preset) {
      if (preset === 'summary') {
        this.pdfOptions = {
          includeDescriptions: false,
          includeNormalSensitivity: false
        };
      } else if (preset === 'detailed') {
        this.pdfOptions = {
          includeDescriptions: true,
          includeNormalSensitivity: false
        };
      } else if (preset === 'full') {
        this.pdfOptions = {
          includeDescriptions: true,
          includeNormalSensitivity: true
        };
      }

      this.persistPdfOptions();
    },
    openPdfOptionsModal() {
      const modalEl = document.getElementById('pdf-options-modal');
      if (modalEl && window.UIkit?.modal) {
        window.UIkit.modal(modalEl).show();
      }
    },
    closePdfOptionsModal() {
      const modalEl = document.getElementById('pdf-options-modal');
      if (modalEl && window.UIkit?.modal) {
        window.UIkit.modal(modalEl).hide();
      }
    },
    async fetchIngredientCheckerAccess() {
      if (!this.kitId) {
        this.ingredientCheckerHasFreeEntitlement = false;
        this.ingredientCheckerEntitlementLabel = '';
        this.ingredientCheckerEntitlementEndsUtc = null;
        return;
      }

      try {
        const response = await axios.get(`${process.env.VUE_APP_API_URL}/api/label-reader/access`, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.accessToken}`,
            'cid': this.cid
          },
          params: {
            kitId: this.kitId
          }
        });

        this.ingredientCheckerHasFreeEntitlement = Boolean(response.data?.has_free_ingredient_checker);
        this.ingredientCheckerEntitlementLabel = response.data?.active_kit_entitlement?.displayLabel || '';
        this.ingredientCheckerEntitlementEndsUtc = response.data?.active_kit_entitlement?.endsUtc || null;
      } catch (error) {
        console.error('Failed to load ingredient checker access for kit:', error);
        this.ingredientCheckerHasFreeEntitlement = false;
        this.ingredientCheckerEntitlementLabel = '';
        this.ingredientCheckerEntitlementEndsUtc = null;
      }
    },
    formatEntitlementDate(value) {
      return new Date(value).toLocaleDateString();
    },
    async fetchData() {
      try {
        this.isLoading = true;

        this.dataSevere = [];
        this.dataMild = [];
        this.dataNormal = [];
        this.groupedSevere = [];
        this.groupedMild = [];
        this.groupedNormal = [];
    
        const testKitNameResponse = await axios.get(`${process.env.VUE_APP_API_URL}/api/get-test-kit-name`, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.accessToken}`,
            'cid': this.cid
          },
          params: {
            id: this.searchTerm
          }
        });

        if (testKitNameResponse.data[0]) {
          this.petName = testKitNameResponse.data[0].PetName;
          this.firstName = testKitNameResponse.data[0].FirstName;
          this.lastName = testKitNameResponse.data[0].LastName;
          this.resultDateUtc = testKitNameResponse.data[0].ResultDateUtc;
          this.testTypeId = testKitNameResponse.data[0].TestTypeId;
          this.typeName = testKitNameResponse.data[0].TypeName;
          this.typeDescription = testKitNameResponse.data[0].TypeDescription;
          this.kitId = testKitNameResponse.data[0].KitId;
          this.serialNumber = testKitNameResponse.data[0].SerialNumber;
          await this.fetchIngredientCheckerAccess();
        }
        
        const headers = {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.accessToken}`,
          'cid': this.cid
        };

        // Fetch all 3 levels
        const [severeRes, mildRes, normalRes] = await Promise.all([
          axios.get(`${process.env.VUE_APP_API_URL}/api/test-results-by-level`, {
            params: { id: this.searchTerm, resultDateUtc: this.resultDateUtc, level: 'severe' },
            headers
          }),
          axios.get(`${process.env.VUE_APP_API_URL}/api/test-results-by-level`, {
            params: { id: this.searchTerm, resultDateUtc: this.resultDateUtc, level: 'mild' },
            headers
          }),
          axios.get(`${process.env.VUE_APP_API_URL}/api/test-results-by-level`, {
            params: { id: this.searchTerm, resultDateUtc: this.resultDateUtc, level: 'normal' },
            headers
          })
        ]);

        const getItemKey = (item) =>
          (item.ItemNameFriendly || item.ItemName || '').trim().toLowerCase();

        // Helper function to deduplicate items within the same list
        const deduplicateItems = (items) => {
          const seen = new Set();
          return items.filter((item) => {
            const key = getItemKey(item);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        };

        // 1. Deduplicate within severe
        const severeDeduped = deduplicateItems(severeRes.data);
        const severeKeys = new Set(severeDeduped.map(getItemKey));

        // 2. Deduplicate within mild and filter out items already in severe
        const mildDeduped = deduplicateItems(mildRes.data);
        const mildFiltered = mildDeduped.filter(
          (item) => !severeKeys.has(getItemKey(item))
        );
        const mildKeys = new Set(mildFiltered.map(getItemKey));

        // 3. Deduplicate within normal and filter out items from severe or mild
        const normalDeduped = deduplicateItems(normalRes.data);
        const normalFiltered = normalDeduped.filter(
          (item) =>
            !severeKeys.has(getItemKey(item)) &&
            !mildKeys.has(getItemKey(item))
        );

        // 4. Assign final values
        this.dataSevere = severeRes.data;
        this.dataMild = mildFiltered;
        this.dataNormal = normalFiltered;

        const respoGroup = await axios.get(`${process.env.VUE_APP_API_URL}/api/get-unique-groups`, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.accessToken}`,
            'cid': this.cid
          },
          params: {
            id: this.searchTerm
          }
        });

        this.uniqueGroupsList = respoGroup.data.map(g => g.GroupName).filter(Boolean);

        this.groupData();
        this.renderReady = true;
        this.isLoading = false;
      } catch (error) {
        console.error('Error fetching items:', error.response ? error.response.data : error.message);
        this.isLoading = false;
      }
    },
    groupData() {
      const processLevel = (dataArray) => {
        let filtered = dataArray;

        // Filter by category and group
        if (this.category !== 'All') {
          filtered = filtered.filter(item => item.CategoryName === this.category);
        }

        if (this.group !== 'All') {
          filtered = filtered.filter(item => item.GroupName === this.group);
        }

        // Deduplicate by ItemId before grouping
        const deduped = Array.from(
          new Map(filtered.map(item => [item.ItemId, item])).values()
        );

        // Group by CategoryName
        return deduped.reduce((acc, item) => {
          const group = acc.find(g => g.CategoryName === item.CategoryName);
          if (group) {
            group.items.push(item);
          } else {
            acc.push({ CategoryName: item.CategoryName, items: [item] });
          }
          return acc;
        }, []);
      };

      this.groupedSevere = processLevel(this.dataSevere);
      this.groupedMild = processLevel(this.dataMild);
      this.groupedNormal = processLevel(this.dataNormal);
    },
    groupItemsByGroupName(items) {
      const grouped = items.reduce((acc, item) => {
        const groupName = item.GroupName || 'Ungrouped';
        if (!acc[groupName]) {
          acc[groupName] = [];
        }
        acc[groupName].push(item);
        return acc;
      }, {});

      // Sort items within each group alphabetically
      Object.keys(grouped).forEach(groupName => {
        grouped[groupName].sort((a, b) => {
          const nameA = (a.ItemNameFriendly || a.ItemName || '').toLowerCase();
          const nameB = (b.ItemNameFriendly || b.ItemName || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
      });

      return grouped;
    },    
    getDescription(item) {
      if (item) {
        if (this.petName) {
          return item.ItemDescriptionPet || item.ItemDescription;
        }
        return item.ItemDescription;
      }
      return '';
    },
    async fetchUniqueGroups() {
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_URL}/api/get-unique-groups`, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.accessToken}`,
            'cid': this.cid
          },
          params: {
            id: this.searchTerm
          }
        });

        this.uniqueGroupsList = response.data.map(g => g.GroupName).filter(Boolean);
      } catch (error) {
        console.error('Error fetching unique groups:', error);
      }
    },
    getGroupExpandKey(sectionKey, categoryName, groupName) {
      return `${sectionKey}__${categoryName}__${groupName || 'Ungrouped'}`;
    },
    isGroupExpanded(sectionKey, categoryName, groupName) {
      const key = this.getGroupExpandKey(sectionKey, categoryName, groupName);
      return !!this.expandedGroups[key];
    },
    toggleGroup(sectionKey, categoryName, groupName) {
      const key = this.getGroupExpandKey(sectionKey, categoryName, groupName);
      this.expandedGroups = {
        ...this.expandedGroups,
        [key]: !this.expandedGroups[key]
      };
    },
    getVisibleItems(items, sectionKey, categoryName, groupName, limit = 10) {
      return this.isGroupExpanded(sectionKey, categoryName, groupName)
        ? items
        : items.slice(0, limit);
    },
    getHiddenCount(items, limit = 10) {
      return Math.max(items.length - limit, 0);
    },
    resetFilters() {
      this.category = 'All';
      this.group = 'All';
    },
    clearSearch() {
      this.search = '';
      this.fetchData();
    },
    formatNumber(value) {
      return value?.toLocaleString() || 0;
    },
    printPage() {
      window.print();
    },
    buildPdfRequestUrl(kitId) {
      const params = new URLSearchParams({
        kitId: String(kitId),
        download: '1'
      });

      if (this.pdfOptions.includeDescriptions) {
        params.set('includeDescriptions', '1');
      }

      if (this.pdfOptions.includeNormalSensitivity) {
        params.set('includeNormalSensitivity', '1');
      }

      return `${process.env.VUE_APP_API_URL}/api/generate-pdf?${params.toString()}`;
    },
    async downloadSelectedPDF() {
      this.persistPdfOptions();
      await this.downloadCurrentPDF();
      this.closePdfOptionsModal();
    },
    async downloadCurrentPDF() {
      const kitId = String(this.kitId);
      if (!kitId) return;

      this.loadingPDF = { ...this.loadingPDF, [kitId]: true };

      try {
        // Ask API to set Content-Disposition: attachment
        const resp = await fetch(this.buildPdfRequestUrl(kitId), {
          headers: { Authorization: `Bearer ${this.accessToken}`, cid: this.cid }
        });
        if (!resp.ok) throw new Error('PDF request failed');

        let { blobUrl, fileName } = await resp.json();
        if (!blobUrl) throw new Error('No blobUrl returned');

        // Optional host rewrite
        blobUrl = blobUrl.replace(
          'https://ucariblob.blob.core.windows.net',
          'https://files.ucari.com'
        );

        // Try to fetch the blob and trigger a download with a friendly filename
        try {
          const fileRes = await fetch(blobUrl, { mode: 'cors', credentials: 'omit' });
          if (!fileRes.ok) throw new Error(`blob fetch failed: ${fileRes.status} ${fileRes.statusText}`);

          const pdfBlob = await fileRes.blob();
          const objectUrl = URL.createObjectURL(pdfBlob);

          const filename =
            fileName ||
            (this.serialNumber ? `${this.serialNumber}.pdf` : `ucari-${kitId}.pdf`);

          const a = document.createElement('a');
          a.href = objectUrl;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(objectUrl);
        } catch (downloadErr) {
          console.warn('Direct download failed (likely CORS/iOS). Falling back to preview:', downloadErr);
          const win = window.open(blobUrl, '_blank');
          if (!win) {
            const a2 = document.createElement('a');
            a2.href = blobUrl;
            a2.target = '_blank';
            a2.rel = 'noopener';
            document.body.appendChild(a2);
            a2.click();
            a2.remove();
          }
        }
      } catch (e) {
        console.error('PDF download failed:', e);
        alert('There was an error downloading your PDF. Please try again.');
      } finally {
        this.loadingPDF = { ...this.loadingPDF, [kitId]: false };
      }
    },
    convertUtcToLocalDateOnly
  },

  watch: {
    category() {
      this.groupData();
    },
    group() {
      this.groupData();
    }
  }
}
</script>

<style scoped>
.pdf-options-modal {
  max-width: 420px;
  border-radius: 12px;
}

.pdf-options-title {
  font-size: 1.4rem;
  line-height: 1.3;
  margin-bottom: 10px;
  text-transform: none;
}

.pdf-options-copy {
  margin-top: -8px;
  color: #5f6b7a;
}

.pdf-preset-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.pdf-preset-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  min-height: 64px;
  padding-left: 12px;
  padding-right: 12px;
  border-width: 1px;
  transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, color 0.15s ease;
}

.pdf-preset-button-active {
  background: #142b6f;
  border-color: #142b6f;
  color: #fff;
  box-shadow: inset 0 0 0 2px #0b173c, 0 0 0 3px rgba(20, 43, 111, 0.18);
}

.pdf-preset-button-active:hover,
.pdf-preset-button-active:focus {
  background: #0f2359;
  border-color: #0f2359;
  color: #fff;
}

.pdf-preset-selected {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.35);
}

.pdf-preset-selected::before {
  content: '✓';
  display: block;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
}

.pdf-option-checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pdf-cancel-button {
  background: #ffffff;
  border: 1px solid #d9e1ea;
  color: #314154 !important;
}

.pdf-cancel-button:hover,
.pdf-cancel-button:focus {
  background: #e4ebf2;
  border-color: #cdd8e3;
  color: #243344;
}

.pdf-download-button[disabled] {
  opacity: 0.9;
  cursor: not-allowed;
}

.how-card,
.action-card-grid {
  width: 100%;
  box-sizing: border-box;
}

.test-results-promo-rail {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.test-results-inline-promos {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 320px));
  gap: 16px;
  align-items: start;
  justify-content: start;
  margin-top: 16px;
  margin-bottom: 24px;
}

.test-results-inline-promos :deep(.ingredient-checker-promo),
.test-results-inline-promos :deep(.nutrable-program-promo),
.test-results-inline-promos :deep(.wellness-promo),
.test-results-inline-promos :deep(.pet-vet-promo) {
  width: 100%;
  margin-left: 0;
}

.test-results-content-layout {
  align-items: flex-start;
}

.how-step-list {
  margin: 10px 0 0;
  padding-left: 18px;
}

.how-step-list li + li {
  margin-top: 4px;
}

.action-card-grid {
  display: block;
  margin-bottom: 20px;
}

.action-card-column {
  min-width: 0;
}

.action-card {
  display: block;
  height: 100%;
  margin: 0;
  max-width: none;
  width: 100%;
  box-sizing: border-box;
}

.action-card--ingredient-checker {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

@media (max-width: 959px) {
  .test-results-promo-rail {
    margin-top: 16px;
    margin-bottom: 24px;
  }

  .test-results-inline-promos {
    grid-template-columns: 1fr;
  }

  .action-card-grid {
    grid-template-columns: 1fr;
  }
}

/* Sidebar on desktop, inline on smaller screens: one promo per product. */
@media (min-width: 1200px) {
  .test-results-inline-promos :deep(.nutrable-program-promo),
  .test-results-inline-promos :deep(.pet-vet-promo) {
    display: none;
  }
}

@media (max-width: 1199px) {
  .test-results-promo-rail {
    display: none;
  }
}

@media print {
  .test-results-content-layout {
    display: block;
  }

  .test-results-promo-rail,
  .test-results-inline-promos,
  #filter-offcanvas,
  #pdf-options-modal {
    display: none !important;
  }
}
</style>
